import {
  ButtonColour,
  ButtonComponent,
  ButtonPriority,
  ButtonSize,
  IconPosition,
} from '@vaad1st/component.atom.button';
import {
  IconActionCrossComponent,
  IconNavigationArrowRightComponent,
  IconNavigationChevronArrowLeftComponent,
} from '@vaad1st/component.atom.icons';
import {
  TypographyComponent,
  TypographySize,
  TypographyVariant,
  TypographyWeight,
} from '@vaad1st/component.atom.typography';
import classnames from 'classnames';
import classNames from 'classnames';
import { createRef, useState } from 'react';
import HTMLRenderer from '../html-renderer/html-renderer.component';
import styles from './route-map.component.module.less';

interface RouteMapProps {
  routeMapPath: string;
  title: string;
}

interface RouteMapComponentProps {
  ESIString: string;
  dataCy?: string;
  mobileTitle: string;
  buttonOnMobile?: boolean;
  buttonText?: string;
}

const RouteMapComponent = ({
  ESIString,
  dataCy = 'route-map',
  mobileTitle = 'Route Map',
  buttonOnMobile = true,
  buttonText = 'Open Route Map',
}: RouteMapComponentProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const routeMapRef = createRef<HTMLDivElement>();

  if (!ESIString) {
    return null;
  }

  const onClose = () => {
    setIsVisible(false);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const setButtonsTabIndex = (index: number) => {
    const buttons = routeMapRef.current?.querySelectorAll('button');
    if (!buttons) {
      return;
    }
    buttons.forEach(button => {
      button.tabIndex = index;
    });
  };

  const focusFirstButton = () => {
    const buttons = routeMapRef.current?.querySelectorAll('button');
    if (!buttons) {
      return;
    }
    buttons[0]?.focus();
  };

  const onAnimationEnd = () => {
    if (!isVisible) {
      setButtonsTabIndex(-1);
    }
    if (isVisible) {
      setButtonsTabIndex(0);
      focusFirstButton();
    }
  };

  return (
    <div
      id="route-map"
      data-cy={dataCy}
      className={classnames(styles['route-map'])}>
      <div
        className={classNames(styles['route-map__content'], {
          [styles['route-map__full-screen']]: buttonOnMobile,
          [styles['map-enter']]: isVisible && buttonOnMobile,
          [styles['map-exit']]: !isVisible && buttonOnMobile,
        })}
        onAnimationEnd={onAnimationEnd}
        ref={routeMapRef}>
        {buttonOnMobile && (
          <div
            className={`${styles['route-map__header']} ${styles['mobile-show']}`}>
            <ButtonComponent
              as="button"
              size={ButtonSize.XSmall}
              aria-label="close"
              colour={ButtonColour.Inverse}
              icon={IconNavigationChevronArrowLeftComponent}
              onClick={onClose}
            />
            <TypographyComponent
              as="h3"
              variant={TypographyVariant.Heading}
              size={TypographySize.Xsm}>
              {mobileTitle}
            </TypographyComponent>
            <ButtonComponent
              as="button"
              colour={ButtonColour.ContrastSecondary}
              size={ButtonSize.XSmall}
              icon={IconActionCrossComponent}
              onClick={onClose}
            />
          </div>
        )}
        <HTMLRenderer id="route-map-esi" htmlString={ESIString} element="div" />
      </div>
      {buttonOnMobile && (
        <div className={styles['route-map__button']}>
          <ButtonComponent
            as="button"
            icon={IconNavigationArrowRightComponent}
            iconPosition={IconPosition.Right}
            text={buttonText}
            size={ButtonSize.Small}
            priority={ButtonPriority.Primary}
            fullWidth={true}
            onClick={() => toggleVisibility()}
          />
        </div>
      )}
    </div>
  );
};

export default RouteMapComponent;
